import Dropzone from 'dropzone'; //eslint-disable-line
import DirectUploadWithRetry from '../lib/direct_upload_with_retry';
import { insertAfter } from '../helpers/dom-helper';

class DirectUploadController {
  constructor(source, file) {
    this.source = source;
    this.file = file;
    this.dropzone = this.source.dropzone;
  }

  start() {
    this.file.controller = this;
    const isMultiple = this.source.dropzoneOptions.uploadMultiple;
    this.hiddenInput = this.createHiddenInput(isMultiple);

    const directUpload = new DirectUploadWithRetry(this.file, this.source.uploadURL, this);
    directUpload.create((error, blob) => {
      if (error) {
        removeElement(this.hiddenInput);
        this.emitDropzoneError(error);
        dispatchEvent(this.source, 'error', { bubbles: false });
      } else {
        this.hiddenInput.value = blob.signed_id;
        this.emitDropzoneSuccess();
        dispatchEvent(this.source, 'success', {
          bubbles: false,
          detail: {
            signedId: blob.signed_id,
            file: this.file,
          },
        });
      }
    });
  }

  // Both phases keep their request on the controller so fileRemoved can abort whichever one is live: the blob
  // POST (including its stale-token retry) or the file store PUT.
  directUploadWillCreateBlobWithXHR(request) {
    this.request = request;
  }

  directUploadWillStoreFileWithXHR(request) {
    this.request = request;
    this.request.upload.addEventListener('progress', (event) => this.directUploadDidProgress(event));
  }

  directUploadDidProgress({ loaded, total }) {
    const progress = (loaded / total) * 100;
    dispatchEvent(this.source, 'file-uploading', {
      detail: {
        loaded,
        total,
        progress,
      },
    });
    this.emitDropzoneUploading();
    this.showProgressBar(progress);
  }

  showProgressBar(progress) {
    if (!this.file) return;

    const previewTemplate = findElement(this.file.previewTemplate, '.dz-upload');
    if (!previewTemplate) return;
    previewTemplate.style.width = `${progress}%`;
  }

  emitDropzoneUploading() {
    if (!this.file) return;

    this.file.status = Dropzone.UPLOADING;
    this.dropzone.emit('processing', this.file);
  }

  emitDropzoneSuccess() {
    if (!this.file) return;

    this.file.status = Dropzone.SUCCESS;
    this.dropzone.emit('success', this.file);
    this.dropzone.emit('complete', this.file);
  }

  emitDropzoneError(error) {
    if (!this.file) return;

    this.file.status = Dropzone.ERROR;
    this.dropzone.emit('error', this.file, error);
    this.dropzone.emit('complete', this.file);
  }

  createHiddenInput(isMultiple) {
    const { fileInput } = this.source;
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = isMultiple ? `${fileInput.name}[]` : fileInput.name;
    insertAfter(input, fileInput);
    return input;
  }
}

export default class DropzoneAreaElement extends HTMLElement {
  defaultImageOptions = {
    acceptedFiles: '.jpg,.jpeg,.png',
    resizeMethod: 'contain',
    resizeWidth: 500,
    resizeMimeType: 'image/jpeg',
  };

  defaultAudioOptions = {
    acceptedFiles: '.ogg,.mp3,.m4a,.wav',
  };

  defaultPdfOptions = {
    acceptedFiles: '.pdf',
    resizeMethod: 'contain',
    resizeMimeType: 'application/pdf',
  };

  defaultAudioVideoOptions = {
    acceptedFiles: '.ogg,.mp3,.m4a,.wav,.mp4,.avi,.mov,.m4v',
  };

  connectedCallback() {
    Dropzone.autoDiscover = false;

    this.disableRailsFileInputField();

    this.dropzone = new Dropzone(this, this.dropzoneOptions);
    this.dropzone.on('addedfile', this.fileAdded.bind(this));
    this.dropzone.on('success', this.success.bind(this));
    this.dropzone.on('removedfile', this.fileRemoved.bind(this));
    this.dropzone.on('canceled', this.canceled.bind(this));
    this.dropzone.on('error', this.error.bind(this));
  }

  disableRailsFileInputField() {
    if (!this.fileInput) return;

    this.fileInput.disabled = true;
    this.fileInput.style.display = 'none';
  }

  fileAdded(file) {
    this.disableSubmitBtn();
    // setTimeout is required for checking `file.accepted`
    setTimeout(() => {
      if (!file.accepted) return;

      const directUpload = new DirectUploadController(this, file);
      directUpload.start();
      dispatchEvent(this, 'file-added', { detail: file });
    }, 500);
  }

  success(file) {
    this.enableSubmitBtn();
    dispatchEvent(this, 'file-upload-success', { detail: file });
  }

  // Dropzone only emits `canceled` for a file whose status is UPLOADING, which this element sets from the store
  // PUT's progress events. A removal during the blob POST arrives here instead, so the abort lives here too.
  fileRemoved(file) {
    this.enableSubmitBtn();
    if (!file.controller) return;

    file.controller.request?.abort();
    removeElement(file.controller.hiddenInput);
    dispatchEvent(this, 'file-removed', { detail: file });
  }

  canceled(file) {
    this.enableSubmitBtn();
    if (!file.controller) return;

    file.controller.request.abort();
    dispatchEvent(this, 'file-upload-canceled', { detail: file });
  }

  error(file) {
    this.enableSubmitBtn();
    dispatchEvent(this, 'file-upload-error', { detail: file });
  }

  enableSubmitBtn() {
    if (!this.submitBtn) return;

    this.submitBtn.disabled = false;
  }

  disableSubmitBtn() {
    if (!this.submitBtn) return;

    this.submitBtn.disabled = true;
  }

  removeAllFiles() {
    this.dropzone.removeAllFiles();
  }

  get fileInput() {
    return this.querySelector('input[type="file"]');
  }

  get uploadURL() {
    if (!this.fileInput) return null;

    return this.fileInput.dataset.directUploadUrl;
  }

  get submitBtn() {
    if (!this.formElement) return null;

    return (
      this.formElement.querySelector('input[type="submit"]') || this.formElement.querySelector('button[type="submit"]')
    );
  }

  get formElement() {
    return this.closest('form');
  }

  get dropzoneOptions() {
    return {
      url: this.uploadURL,
      headers: {
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
        Accept: 'application/json',
      },
      autoQueue: false,
      addRemoveLinks: true,
      paramName: 'file[]',
      maxFiles: 1,
      maxFilesize: 2,
      uploadMultiple: false,
      dictDefaultMessage: 'Upload Files Here',
      ...(this.contentType === 'image' ? this.defaultImageOptions : {}),
      ...(this.contentType === 'pdf' ? this.defaultPdfOptions : {}),
      ...(this.contentType === 'audio' ? this.defaultAudioOptions : {}),
      ...(this.contentType === 'audio_video' ? this.defaultAudioVideoOptions : {}),
      ...this.userPassedOptions,
    };
  }

  get userPassedOptions() {
    return JSON.parse(this.dataset.options);
  }

  get contentType() {
    return this.dataset.contentType;
  }
}

function findElement(root, selector) {
  if (typeof root === 'string') {
    /* eslint-disable no-param-reassign */
    selector = root;
    root = document;
    /* eslint-enable no-param-reassign */
  }
  return root?.querySelector(selector);
}

function removeElement(el) {
  if (el && el.parentNode) {
    el.parentNode.removeChild(el);
  }
}

function dispatchEvent(element, eventName, options = {}) {
  element.dispatchEvent(
    // eslint-disable-next-line no-new
    new CustomEvent(`dropzone-area:${eventName}`, { bubbles: true, ...options })
  );
}
